import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// A preview ("view as member") session lives in sessionStorage so it is isolated to ONE tab and
// never replaces the admin's own login (kept in localStorage).
const PREVIEW_KEY = "pg_preview_token";
let currentToken = sessionStorage.getItem(PREVIEW_KEY) || localStorage.getItem("pg_token") || null;

export function isPreviewSession() {
  return !!sessionStorage.getItem(PREVIEW_KEY);
}

export function startPreviewSession(token) {
  sessionStorage.setItem(PREVIEW_KEY, token);
  currentToken = token;
}

export function endPreviewSession() {
  sessionStorage.removeItem(PREVIEW_KEY);
  currentToken = localStorage.getItem("pg_token") || null;
}

export function setAuthToken(token) {
  if (isPreviewSession()) {
    // Logging out / expiring inside a preview tab only ends the preview.
    if (!token) { endPreviewSession(); return; }
    startPreviewSession(token);
    return;
  }
  currentToken = token;
  if (token) localStorage.setItem("pg_token", token);
  else localStorage.removeItem("pg_token");
}

export function getAuthToken() {
  return currentToken;
}

api.interceptors.request.use((config) => {
  if (currentToken) {
    config.headers.Authorization = `Bearer ${currentToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    // Auto-logout on auth failure for protected calls (not for login attempts)
    if ((status === 401 || status === 403) && !url.includes("/auth/login") && !url.includes("/auth/2fa")) {
      if (currentToken) {
        const wasPreview = isPreviewSession();
        setAuthToken(null);
        if (wasPreview) {
          window.location.href = "/admin?preview_ended=1";
        } else if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login?expired=1";
        }
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Public data the HTML shell started fetching before the JavaScript bundle arrived (see public/index.html).
 * Returns an axios-shaped promise ({ data }) once, then falls back to a normal request.
 */
export function getPrefetched(key, path) {
  const pre = typeof window !== "undefined" && window.__pgPrefetch && window.__pgPrefetch[key];
  if (pre) {
    delete window.__pgPrefetch[key];
    return pre.then((data) => ({ data })).catch(() => api.get(path));
  }
  return api.get(path);
}

export function apiError(err, fallback = "Something went wrong") {
  return err?.response?.data?.detail || fallback;
}
